// Component-based presentation adapter. Product, variant, editor and cart logic
// remain owned by VLDProduct/VLDEditor; no second personalisation engine.
const esc = value => String(value ?? '').replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const labels = {front:'Front',inside:'Inside',back:'Back',gift:'Gift box'};
const build = new URL(import.meta.url).searchParams.get('v') || 'local';
const optionIndex = (cfg,name) => cfg.product.options.findIndex(o=>(o.name||o).toLowerCase()===name.toLowerCase());
function offered(cfg,view) {
  const i=optionIndex(cfg,`${view} Engraving`);
  return i>=0 && cfg.product.variants.some(v=>/^(yes|true|1)$/i.test(v.options[i]));
}
function SurfaceFields(view,cfg) {
  const copy=cfg.editorialCopy.surfaces[view];
  return `<section class="vld-addon" data-addon="${view}" aria-label="${labels[view]} engraving">
    <label class="vld-addon__head"><span><input type="checkbox" data-toggle> Add ${view} engraving</span><span data-addon-price></span></label>
    <div class="vld-addon__body" hidden>
      <label class="studio-label">${esc(copy.label)}
        <div class="vld-addon__field"><input type="text" maxlength="40" autocomplete="off" aria-label="${labels[view]} engraving text" placeholder="Make it personal"><span class="vld-addon__count" data-count>0/40</span></div>
      </label>
      <p class="studio-guidance">${esc(copy.hint)}</p>
    </div>
  </section>`;
}
function GiftFields(cfg) {
  const gift=cfg.gift?.variants?.length?cfg.gift:cfg.giftFallback;
  if(!gift?.variants?.length)return '';
  return `<section class="vld-addon" data-addon="gift"><label class="vld-addon__head"><span><input type="checkbox" data-toggle> Add a gift box</span><span data-addon-price></span></label>
    <div class="vld-addon__body" hidden><div class="vld-choices">${gift.variants.map((v,i)=>`<button class="vld-choice ${i?'':'on'}" data-gift-variant="${esc(v.id)}" type="button">${v.image?`<img src="${esc(v.image)}" alt="">`:''}<span>${esc(v.title)}</span></button>`).join('')}</div></div></section>`;
}
function ProductPreview(cfg) {
  return `<section class="studio-preview" aria-label="Your personalised product">
    <div class="studio-preview-heading"><span>Live preview</span><span data-preview-label>Front</span></div>
    <div class="luxury-media"><div class="luxury-media__viewport">
      <div class="studio-preview-status" role="status" data-preview-status><span>Preparing your preview…</span><button type="button" data-preview-retry hidden>Try again</button></div>
      <div class="vld-stage"></div>
      <div class="vld-alt" data-vld-alt><canvas class="vld-alt__bg" hidden></canvas><canvas class="vld-alt__canvas"></canvas><img class="vld-alt__img" alt="Selected product view" hidden></div>
    </div></div>
    <p class="studio-preview-note">Your words. Your design. See it as you make it.</p>
  </section>`;
}
function Studio(cfg) {
  return `<div class="studio" data-vld-product data-active="front" data-preview-state="loading">
    <script type="application/json" data-vld-config></script>
    <nav class="studio-return" aria-label="Back to product"><a href="${esc(cfg.productUrl)}">← Back to product</a></nav>
    <main class="studio-layout">${ProductPreview(cfg)}
      <section class="studio-editor" aria-label="Personalisation controls">
        <div class="studio-title"><span class="studio-eyebrow">Make it yours</span><h1>${esc(cfg.productTitle)}</h1><p>${esc((cfg.customisation?.mode==='Image'?'Make it yours with an image that means something to you.':cfg.editorialCopy.designs[String(cfg.designId)]?.intro)||'Choose the details that make it personal to you.')}</p></div>
        <div class="studio-colour"><label>Colour <strong data-vld-colour-name>${esc(cfg.initialColour)}</strong></label><div data-vld-swatches class="lux-swatches"></div><div data-vld-options></div></div>
        <nav class="vld-views studio-tabs" data-vld-views aria-label="Engraving area"></nav>
        <div class="studio-section-title"><h2 data-editor-title>Front engraving</h2><span data-section-description>Included</span></div>
        <div data-vld-slot="panel-desktop"><div class="vld-panel">
          <div class="vld-editor-slot" aria-label="Front design"></div>
          <details open class="vld-addons" data-vld-addons><summary class="vld-addons__summary">Options</summary><div class="vld-addons__list">
            ${['inside','back'].filter(v=>offered(cfg,v)).map(v=>SurfaceFields(v,cfg)).join('')}${GiftFields(cfg)}
          </div></details>
        </div></div>
        <div data-vld-slot="buy-desktop"><div class="vld-buy studio-buy">
          <div class="studio-total"><span>Your total</span><div><s data-vld-compare></s> <strong data-vld-price></strong></div></div>
          <button class="vld-atc" data-vld-atc type="button"><span>Add to cart</span></button>
          <p class="vld-err" data-vld-err hidden role="alert"></p>
          <div class="vld-voila" data-vld-voila role="status">Added to your bag.</div>
          
        </div></div>
      </section>
    </main>
  </div>`;
}
function loadScript(src) {
  return new Promise((resolve,reject)=>{
    const script=document.createElement('script');script.src=src;script.onload=resolve;script.onerror=reject;document.head.append(script);
  });
}
function adaptFrontFields(root) {
  root.querySelectorAll('.vld-ed__field').forEach(field=>{
    const text=field.querySelector('textarea'), name=field.querySelector(':scope > label');
    // Editorial roles are data, not guesses from sample names such as Harry.
    // Idempotent writes avoid retriggering this observer endlessly.
    if(text && name) {
      const copy=root.__editorialCopy,fieldCopy=root.__fieldRoles?.[field.dataset.id]||copy.designs[root.__designId]?.fields[field.dataset.id];
      const role=fieldCopy?.role||(field.dataset.id.startsWith('add')?'extra':'words');
      const wording={...copy.roles[role],...fieldCopy};
      if(name.textContent!==wording.label)name.textContent=wording.label;
      text.setAttribute('aria-label',wording.label);
      let hint=field.querySelector('[data-field-guidance]');
      if(!hint){hint=document.createElement('p');hint.className='studio-guidance';hint.dataset.fieldGuidance='';hint.id='guidance-'+field.dataset.id;text.after(hint);}
      if(hint.textContent!==wording.hint)hint.textContent=wording.hint;
      text.setAttribute('aria-describedby',hint.id);
    }
    const chips=[...field.querySelectorAll('.vld-ed__chip')];
    if(!chips.length)return;

  });
  root.querySelectorAll('.vld-ed__arts').forEach(art=>{
    if(art.closest('.studio-art-options'))return;
    const details=document.createElement('details');details.className='studio-art-options';details.open=['Image','Both'].includes(root.__customisationMode);
    const summary=document.createElement('summary');summary.textContent='Artwork options';
    details.append(summary);art.before(details);details.append(art);
  });
}
async function start() {
  const host=document.querySelector('#studio-app');
  try {
    const cfg=JSON.parse(document.querySelector('[data-shopify-studio-config]').textContent);cfg.inlineStudio=true;
    const selected=new URLSearchParams(location.search).get('colour');if(cfg.swatches.some(s=>s.colour===selected))cfg.initialColour=selected;
    cfg.editorialCopy=await fetch(cfg.copyUrl).then(r=>{if(!r.ok)throw Error('Personalisation wording unavailable');return r.json()});
    // Local region calibration candidates are separate from the product snapshot.
    const regions=await fetch(cfg.surfaceRegionsUrl);
    if(!regions.ok)throw Error('Engraving regions unavailable');
    cfg.surfaceRegions=await regions.json();
    document.title=`${cfg.productTitle} · Personalise · VickersLDN`;
    host.innerHTML=Studio(cfg);
    const root=host.querySelector('[data-vld-product]');
    root.__fieldRoles=cfg.customisation?.field_roles;root.__customisationMode=cfg.customisation?.mode;root.__editorialCopy=cfg.editorialCopy;root.__designId=String(cfg.designId);
    root.dataset.build=build;
    root.addEventListener('vld:editor-status', e=>{
      const ready=e.detail.ready;
      root.dataset.previewState=ready?'ready':'failed';
      const status=root.querySelector('[data-preview-status]');
      status.hidden=ready;
      if(!ready)status.querySelector('span').textContent='Your preview couldn’t load. Please try again.';
      root.querySelector('[data-preview-retry]').hidden=ready;
    });
    root.querySelector('[data-preview-retry]').addEventListener('click',()=>location.reload());
    root.querySelector('[data-vld-config]').textContent=JSON.stringify(cfg);
    host.removeAttribute('aria-live');
    const tabs=root.querySelector('[data-vld-views]'), homeSlot=document.createComment('surface navigation');
    tabs.before(homeSlot);
    const mobile=matchMedia('(max-width:989px)');
    const placeTabs=()=>{
      if(mobile.matches)root.querySelector('.studio-preview').append(tabs);
      else homeSlot.after(tabs);
    };
    placeTabs();mobile.addEventListener('change',placeTabs);
    let restoring=false;
    const activate=(view,history=true)=>{
      root.dataset.active=view;
      root.querySelector('[data-preview-label]').textContent=labels[view]||'Detail';
      root.querySelector('[data-editor-title]').textContent=view==='gift'?'Finishing touches':`${labels[view]||'Product'} engraving`;
      root.querySelector('[data-section-description]').textContent=view==='front'?'Included':'Optional';
      if(history) {
        const url=new URL(location.href);
        if(url.searchParams.get('surface')!==view) {
          url.searchParams.set('surface',view);
          window.history.pushState({surface:view},'',url);
        }
      }
    };
    root.addEventListener('vld:view',e=>activate(e.detail.view,!restoring));
    const observer=new MutationObserver(()=>adaptFrontFields(root));
    observer.observe(root.querySelector('.vld-editor-slot'),{childList:true,subtree:true,attributes:true,attributeFilter:['aria-checked']});
    for(const name of ['vld-design.js','vld-editor.js','vld-product.js'])await loadScript(cfg.assetUrls[name]);
    VLDProduct.mount(root);
    const view=new URL(location.href).searchParams.get('surface')||'front';
    root.querySelector(`[data-vld-views] [data-view="${CSS.escape(view)}"]`)?.click();
    window.addEventListener('popstate',()=>{
      const v=new URL(location.href).searchParams.get('surface')||'front';
      restoring=true;
      root.querySelector(`[data-vld-views] [data-view="${CSS.escape(v)}"]`)?.click();
      restoring=false;
    });
    window.studioReady=true;
  } catch(error) {
    host.replaceChildren();
    const message=document.createElement('p');message.className='studio-load-error';message.textContent='We couldn’t load your product. Please refresh to try again.';host.append(message);
    console.error(error);
  }
}
start();
